import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useController, useWatch } from "react-hook-form";
import LocationSearchInput from "@components/input/locationSearchInput";
import FormSimple from "@components/formGroup/formSimple";
import { useNotification } from "@hooks/useNotification";

export const useSectionAdressColumns = () => {
  const { t } = useTranslation("pages.employés.infoPers");

  return useMemo(() => [
    { id: "firstAdress", label: t("Adresse") },
    { id: "secondAdress", label: t("Adresse complémentaire") }
  ], [t])
}

export const SectionAdressTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");

  return (
    <>
      <th
        key="firstAdress"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstAdress ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Adresse")}
      </th>
      <th
        key="secondAdress"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.secondAdress ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Adresse complémentaire")}
      </th>
    </>
  );
};

export const SectionAdressTableBulkCells = ({
  control,
  employeeIndex,
  employee,
  readOnly = false,
  setValue,
  getValues,
  mustWait,
  hiddenColumns = {}
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");
  const { showInfo } = useNotification();
  const readOnlyAccess = mustWait || readOnly;

  const firstAdressWatcher = useWatch({
    control,
    name: `employees.${employeeIndex}.adress.firstAdress`
  });

  const { field: zipCodeField } = useController({
    control,
    name: `employees.${employeeIndex}.adress.zipCode`,
    defaultValue: employee.adress?.zipCode || ""
  });
  const { field: provinceField } = useController({
    control,
    name: `employees.${employeeIndex}.adress.province`,
    defaultValue: employee.adress?.province || ""
  })
  const { field: cityField } = useController({
    control,
    name: `employees.${employeeIndex}.adress.city`,
    defaultValue: employee.adress?.city || ""
  });
  const { field: countryField } = useController({
    control,
    name: `employees.${employeeIndex}.adress.country`,
    defaultValue: employee.adress?.country || ""
  });
  const { field: countryCodeField } = useController({
    control,
    name: `employees.${employeeIndex}.adress.countryCode`,
    defaultValue: employee.adress?.countryCode || ""
  });

  function handleSelectLocation(firstAdress, additionalInfo) {
    if (additionalInfo) {
      if (!additionalInfo.latLng) return showInfo("information_adress_incomplete");

      setValue(`employees.${employeeIndex}.adress.firstAdress`, firstAdress);
      zipCodeField.onChange(additionalInfo.postalCode);
      provinceField.onChange(additionalInfo.province);
      cityField.onChange(additionalInfo.city);
      countryField.onChange(additionalInfo.country);
      countryCodeField.onChange(additionalInfo.countryCode);
    }
  }

  function handleChangeLocation() {
    const address = getValues(`employees.${employeeIndex}.adress`);
    if (address) {
      setValue(`employees.${employeeIndex}.adress.firstAdress`, "");
      zipCodeField.onChange("");
      provinceField.onChange("");
      cityField.onChange("");
      countryField.onChange("");
      countryCodeField.onChange("");
    }
  };

  return (
    <>
      <td
        key="firstAdress"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.firstAdress ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.adress.firstAdress`}
          defaultValue={employee.adress?.firstAdress || ""}
          render={({ field: { value } }) => (
            <LocationSearchInput
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
              isValid={true}
              onSelect={handleSelectLocation}
              onChange={handleChangeLocation}
              defaultValue={firstAdressWatcher || value}
              isDisabled={readOnlyAccess}
            />
          )}
        />
      </td>
      <td
        key="secondAdress"
        className={`arh-w-[12.5rem] arh-min-w-[12.5rem] arh-max-w-[12.5rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.secondAdress ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.adress.secondAdress`}
          defaultValue={employee.adress?.secondAdress || ""}
          render={({ field: { onChange, value } }) => (
            <FormSimple
              placeholder={mainTranslator("Adresse (suite)")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={readOnlyAccess}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
            />
          )}
        />
      </td>
    </>
  );
};

export const getAdressColumnCount = () => 2;